import { z } from 'zod';
import { CURRENCIES, ITEM_PRIORITIES, LIST_VISIBILITIES, LIMITS, RESERVATION_STATUSES, USERNAME_RE, } from './constants.js';
/* ------------------------------------------------------------------ */
/* Примитивы                                                           */
/* ------------------------------------------------------------------ */
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(LIMITS.emailMax)
    .email('Похоже, это не адрес почты');
export const passwordSchema = z
    .string()
    .min(LIMITS.passwordMin, `Пароль должен быть не короче ${LIMITS.passwordMin} символов`)
    .max(LIMITS.passwordMax);
export const usernameSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(USERNAME_RE, 'Только латиница, цифры и _, начинается с буквы, от 3 до 32 символов');
export const displayNameSchema = z
    .string()
    .trim()
    .min(LIMITS.displayNameMin, 'Имя не может быть пустым')
    .max(LIMITS.displayNameMax);
/**
 * URL, который мы готовы принять от пользователя и пойти по нему.
 * Только http/https — никаких file://, javascript:, data: (защита от SSRF на уровне схемы,
 * сетевой уровень защиты — в scraper/src/safe-fetch.ts).
 */
export const httpUrlSchema = z
    .string()
    .trim()
    .max(LIMITS.urlMax)
    .refine((value) => {
    try {
        const parsed = new URL(value);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    }
    catch {
        return false;
    }
}, 'Нужна ссылка, начинающаяся с http:// или https://');
/**
 * Ссылка на картинку.
 *
 * Отличается от обычного URL тем, что принимает ещё и наш внутренний путь
 * `/uploads/...` — так выглядит фотография, загруженная пользователем со своего
 * телефона. Без этого послабления любое загруженное фото отклонялось бы
 * валидацией: относительный путь — это не `http://`.
 */
export const imageRefSchema = z
    .string()
    .trim()
    .max(LIMITS.urlMax)
    .refine((value) => {
    // Наш загруженный файл: строго /uploads/ + имя, без обхода каталогов.
    if (/^\/uploads\/[A-Za-z0-9._-]+$/.test(value))
        return true;
    try {
        const parsed = new URL(value);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    }
    catch {
        return false;
    }
}, 'Нужна ссылка на картинку (http://…) или загруженный файл');
export const currencySchema = z.enum(CURRENCIES);
export const priceMinorSchema = z.number().int().min(0).max(1_000_000_000_00);
/* ------------------------------------------------------------------ */
/* Аутентификация                                                      */
/* ------------------------------------------------------------------ */
export const registerSchema = z.object({
    email: emailSchema,
    password: passwordSchema,
    displayName: displayNameSchema,
    username: usernameSchema.optional(),
});
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1).max(LIMITS.passwordMax),
});
export const updateProfileSchema = z
    .object({
    displayName: displayNameSchema.optional(),
    username: usernameSchema.optional(),
    bio: z.string().trim().max(500).optional(),
    avatarUrl: imageRefSchema.nullable().optional(),
    birthday: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата в формате ГГГГ-ММ-ДД')
        .nullable()
        .optional(),
})
    .refine((value) => Object.keys(value).length > 0, 'Нечего обновлять');
export const forgotPasswordSchema = z.object({
    email: emailSchema,
});
export const resetPasswordSchema = z.object({
    token: z.string().trim().min(20).max(200),
    newPassword: passwordSchema,
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1).max(LIMITS.passwordMax),
    newPassword: passwordSchema,
});
/**
 * Удаление аккаунта.
 *
 * Пароль запрашивается не из вредности: чужой незаблокированный телефон —
 * самый реальный способ потерять аккаунт вместе со всеми списками.
 */
export const deleteAccountSchema = z.object({
    password: z.string().min(1).max(LIMITS.passwordMax),
});
/* ------------------------------------------------------------------ */
/* Списки                                                              */
/* ------------------------------------------------------------------ */
export const createListSchema = z.object({
    title: z.string().trim().min(1, 'Дайте списку название').max(LIMITS.listTitleMax),
    description: z.string().trim().max(LIMITS.listDescriptionMax).optional(),
    visibility: z.enum(LIST_VISIBILITIES).default('friends'),
    eventDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .optional(),
    coverEmoji: z.string().trim().max(8).nullable().optional(),
});
export const updateListSchema = createListSchema.partial().extend({
    isArchived: z.boolean().optional(),
});
/* ------------------------------------------------------------------ */
/* Желания                                                             */
/* ------------------------------------------------------------------ */
export const createItemSchema = z.object({
    title: z.string().trim().min(1, 'Как называется желание?').max(LIMITS.itemTitleMax),
    description: z.string().trim().max(LIMITS.itemDescriptionMax).optional(),
    url: httpUrlSchema.nullable().optional(),
    imageUrl: imageRefSchema.nullable().optional(),
    /** Все найденные картинки — чтобы пользователь мог переключить обложку без повторного парсинга. */
    imageCandidates: z.array(imageRefSchema).max(LIMITS.imagesPerItem).optional(),
    priceMinor: priceMinorSchema.nullable().optional(),
    currency: currencySchema.nullable().optional(),
    priority: z.enum(ITEM_PRIORITIES).default('normal'),
    quantity: z.number().int().min(1).max(LIMITS.maxQuantity).default(1),
    sourceSite: z.string().trim().max(120).nullable().optional(),
    /**
     * Складчина: на дорогой подарок можно скинуться нескольким людям.
     * Тогда вместо «кто-то один взял» получается общий сбор.
     */
    groupGift: z.boolean().default(false),
});
export const updateItemSchema = createItemSchema.partial().extend({
    isArchived: z.boolean().optional(),
    listId: z.string().uuid().optional(),
});
export const reorderItemsSchema = z.object({
    itemIds: z.array(z.string().uuid()).min(1).max(LIMITS.itemsPerList),
});
/* ------------------------------------------------------------------ */
/* Брони                                                               */
/* ------------------------------------------------------------------ */
export const createReservationSchema = z.object({
    quantity: z.number().int().min(1).max(LIMITS.maxQuantity).default(1),
    status: z.enum(RESERVATION_STATUSES).default('reserved'),
    note: z.string().trim().max(LIMITS.itemNoteMax).optional(),
    /** Скрыть себя даже от других дарителей — они увидят «кто-то забронировал». */
    anonymous: z.boolean().default(false),
    /** Вклад в складчину, в копейках. Только для желаний со сбором. */
    contributionMinor: priceMinorSchema.nullable().optional(),
});
export const updateReservationSchema = z.object({
    status: z.enum(RESERVATION_STATUSES).optional(),
    quantity: z.number().int().min(1).max(LIMITS.maxQuantity).optional(),
    note: z.string().trim().max(LIMITS.itemNoteMax).optional(),
    anonymous: z.boolean().optional(),
    contributionMinor: priceMinorSchema.nullable().optional(),
});
/* ------------------------------------------------------------------ */
/* Друзья                                                              */
/* ------------------------------------------------------------------ */
export const friendRequestSchema = z.object({
    /** Кого зовём: либо @username, либо id. */
    username: usernameSchema.optional(),
    userId: z.string().uuid().optional(),
}).refine((v) => Boolean(v.username || v.userId), 'Укажите пользователя');
export const searchUsersSchema = z.object({
    q: z.string().trim().min(2, 'Минимум 2 символа').max(64),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
/* ------------------------------------------------------------------ */
/* Парсинг ссылок                                                      */
/* ------------------------------------------------------------------ */
export const previewLinkSchema = z.object({
    url: httpUrlSchema,
    /** Игнорировать кэш и сходить на сайт заново. */
    refresh: z.boolean().default(false),
});
//# sourceMappingURL=schemas.js.map